import React from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  IgAccordion,
  IgCheckbox,
  IgFullPageDialog,
  IgTextField,
} from "@intelligems/ig-theme";
import { ProgressBarWidgetDto } from "@intelligems/ig-types";
import { FormControlLabel, Grid, InputAdornment } from "@mui/material";
import { ColorAndStyle } from "@src/components/ColorAndStyle/ColorAndStyle";
import { GlobalStylesHeader } from "@src/domains/WidgetLibrary/GlobalStylesHeader";
import { ProgressBarPreview } from "@src/domains/WidgetLibrary/ProgressBar/ProgressBarPreview";
import { useWidgetSave } from "@src/domains/WidgetLibrary/useWidgetSave";
import { ShippingProgressBarSettings } from "@src/features/personalizations/components/offerModifications/userInterfaces/progressBar/ShippingProgressBarSettings";
import { OfferComponentSettingsPageWrapper } from "@src/features/sharedExperience/components/offers/subpages/OfferComponentSettingsPageWrapper";
import { useViewPage } from "@src/hooks/mixpanel";
import {
  getDialogPropDetails,
  isDialogOpen,
  setDialog,
} from "@src/store/slices/global";
import {
  getProgressBar,
  selectedWidget,
  setWidgetConfig,
  setWidgetProperty,
} from "@src/store/slices/widgets";
import { useShippingProgressBarConfig } from "@src/store/slices/widgets/hooks/useShippingProgressBarConfig";
import { useCurrency } from "@src/utils/useCurrency";

export const ShippingProgressBarFullPageDialog = () => {
  const dispatch = useDispatch();
  const { symbol } = useCurrency();
  const isOpen = useSelector(isDialogOpen("shippingProgressBarFullPageEditor"));
  const dialogProps = useSelector(
    getDialogPropDetails("shippingProgressBarFullPageEditor")
  ) as
    | {
        widgetId?: string;
        variationId: string;
        widgetType: "shippingProgressBar";
      }
    | undefined;

  const currentWidget = useSelector(selectedWidget) as ProgressBarWidgetDto;
  const underThresholdText = useShippingProgressBarConfig(
    "variables.textReplacements.buyMoreTemplate"
  );

  const aboveThresholdText = useShippingProgressBarConfig(
    "variables.textReplacements.currentUnlockedTemplate"
  );

  const { save, isSaving } = useWidgetSave(currentWidget);

  const handleClose = () => {
    dispatch(
      setDialog({ dialog: "shippingProgressBarFullPageEditor", open: false })
    );
  };

  const handleSave = () => {
    save();
    handleClose();
  };

  const handleCancel = () => {
    handleClose();
  };
  useViewPage("Global Styles - Shipping Progress Bar Full Page Editor");
  if (!dialogProps?.widgetId || !currentWidget) return null;

  return (
    <IgFullPageDialog
      showDialog={isOpen}
      handleClose={handleClose}
      content={
        <Grid container width={"100%"} justifyContent={"center"}>
          <Grid container direction={"column"} rowSpacing={4} width={"1250px"}>
            <Grid item>
              <GlobalStylesHeader
                variantStyle={
                  currentWidget.config.desktop?.variant || "progressBar1"
                }
                widgetType={"shippingProgressBar"}
              />
            </Grid>
            <Grid item>
              <OfferComponentSettingsPageWrapper
                variationId={dialogProps.variationId}
                widgetType="shippingProgressBar"
                hasQuantityButtons={false}
                hasProgressbar={true}
                hasMessageBox={false}
                offerProducts={[]}
                widgetId={dialogProps.widgetId}
                customPreview={
                  <ProgressBarPreview
                    mode={"toggle"}
                    widget={currentWidget}
                    widgetOverride={currentWidget}
                    isEdit={true}
                  />
                }
              >
                <IgAccordion title={"Settings"} defaultExpanded={true}>
                  <Grid item container direction={"column"}>
                    {currentWidget.enabledSitewide && (
                      <Grid item sx={{ width: "210px" }}>
                        <IgTextField
                          label={"Minimum purchase requirement"}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                {symbol}
                              </InputAdornment>
                            ),
                          }}
                          value={
                            currentWidget.config.desktop?.variables
                              ?.defaultShippingThreshold || ""
                          }
                          onChange={(e) =>
                            dispatch(
                              setWidgetConfig({
                                key: "variables.defaultShippingThreshold",
                                value: e.target.value,
                              })
                            )
                          }
                        />
                      </Grid>
                    )}
                  </Grid>

                  <ShippingProgressBarSettings
                    variationId={dialogProps.variationId}
                    widgetVariantType={
                      currentWidget.config.desktop?.variant || "progressBar1"
                    }
                    underThresholdText={underThresholdText[0]}
                    setUnderThresholdText={underThresholdText[1]}
                    aboveThresholdText={aboveThresholdText[0]}
                    setAboveThresholdText={aboveThresholdText[1]}
                  />
                </IgAccordion>
              </OfferComponentSettingsPageWrapper>
            </Grid>
          </Grid>
        </Grid>
      }
      yesButtonText={isSaving ? "Saving..." : "Save Widget"}
      handleYesClick={handleSave}
      noButtonText="Cancel"
      handleNoClick={handleCancel}
      shouldNotClose={true}
      hideActions={false}
    />
  );
};
